import { MoreHorizontal, Plus, Trash2 } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useViewOnly } from '@/components/templates/app-shell'
import type { ContactGroupMember } from '@/pages/ContactGroupsPage'

export function GroupDetailActions({
  onAdd,
  onRename,
  onDelete,
}: {
  onAdd: () => void
  onRename: () => void
  onDelete: () => void
}) {
  return (
    <>
      <Button type="button" onClick={onAdd}>
        <Plus className="mr-2 h-4 w-4" aria-hidden="true" />
        Add Contacts
      </Button>
      <Button type="button" variant="quietDestructive" onClick={onDelete}>
        <Trash2 className="mr-2 h-4 w-4" aria-hidden="true" strokeWidth={1.5} />
        Delete Group
      </Button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button type="button" variant="outline" size="sm" aria-label="Group actions">
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onSelect={onRename}>Rename</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  )
}

export function GroupMembersTable({
  members,
  onRemove,
}: {
  members: ContactGroupMember[]
  onRemove: (id: number) => void
}) {
  const viewOnly = useViewOnly()
  if (members.length === 0) {
    return (
      <div className="rounded-md border border-border bg-card p-6 text-center">
        <p className="text-body-sm font-medium text-foreground">No Contacts in this Group</p>
      </div>
    )
  }

  return (
    <div className="overflow-hidden rounded-md border border-border bg-card">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Contact type</TableHead>
            <TableHead>Status</TableHead>
            {!viewOnly && <TableHead className="w-12" aria-label="Row actions" />}
          </TableRow>
        </TableHeader>
        <TableBody>
          {members.map((member) => (
            <TableRow key={member.id}>
              <TableCell className="font-medium">{member.name}</TableCell>
              <TableCell>{member.contact_type}</TableCell>
              <TableCell>
                {member.archived ? <Badge variant="warning">Archived</Badge> : 'Current'}
              </TableCell>
              {!viewOnly && (
                <TableCell>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        aria-label={`${member.name} actions`}
                      >
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem destructive onSelect={() => onRemove(member.id)}>
                        Remove from Group
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  )
}
